import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMundusStore } from '../stores/mundus.store';
import { AchievementGallery } from '../components/AchievementGallery';
import { AchievementProgress } from '../components/AchievementProgress';
import { getAchievementProgress } from '../lib/achievements.api';
import { AchievementProgress as AchievementProgressType } from '../types/mundus';

export default function Achievements() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentMundus, fetchMundusById } = useMundusStore();
  const [achievementProgress, setAchievementProgress] = useState<AchievementProgressType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadData(id);
    }
  }, [id]);

  const loadData = async (mundusId: string) => {
    try {
      setIsLoading(true);
      await Promise.all([
        fetchMundusById(mundusId),
        loadAchievementProgress(mundusId),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAchievementProgress = async (mundusId: string) => {
    try {
      const progress = await getAchievementProgress(mundusId);
      setAchievementProgress(progress);
    } catch (error) {
      console.error('Error loading achievement progress:', error);
    }
  };

  const getTemplateIcon = (goalType: string) => {
    const icons: Record<string, string> = {
      IKIGAI: '⚡',
      CLIMATE: '🌐',
      CAREER: '🚀',
    };
    return icons[goalType] || '💫';
  };

  const getTemplateColors = (goalType: string) => {
    const colors: Record<string, { border: string; glow: string; text: string }> = {
      IKIGAI: { border: 'border-purple-600', glow: 'shadow-lg', text: 'text-purple-600' },
      CLIMATE: { border: 'border-green-600', glow: 'shadow-lg', text: 'text-green-600' },
      CAREER: { border: 'border-blue-600', glow: 'shadow-lg', text: 'text-blue-600' },
    };
    return colors[goalType] || { border: 'border-blue-600', glow: 'shadow-lg', text: 'text-blue-600' };
  };

  const colors = currentMundus ? getTemplateColors(currentMundus.goalType) : { border: 'border-blue-600', glow: 'shadow-lg', text: 'text-blue-600' };

  if (isLoading || !currentMundus) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-slate-600">
            Loading achievements...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className={`bg-white border-b border-slate-200 ${colors.glow}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(`/mundus/${id}`)}
            className="text-sm text-slate-600 hover:text-blue-600 mb-4 flex items-center transition-colors group"
          >
            <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Mundus</span>
          </button>

          <div className="flex items-center space-x-4">
            <div className={`text-5xl w-16 h-16 rounded-lg border-2 ${colors.border} flex items-center justify-center bg-slate-50`}>
              {getTemplateIcon(currentMundus.goalType)}
            </div>
            <div>
              <h1 className={`text-3xl font-bold ${colors.text} mb-1`}>
                Achievements
              </h1>
              <p className="text-sm text-slate-600">
                {currentMundus.name}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Track your progress and unlock achievements
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Achievement Gallery */}
          <div className="lg:col-span-2">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center space-x-2">
                <span>🏆</span>
                <span>Achievement <span className={colors.text}>Gallery</span></span>
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Collect all {achievementProgress?.total || 0} achievements
              </p>
            </div>
            <AchievementGallery mundusId={id!} />
          </div>

          {/* Sidebar */}
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-bold text-slate-900 flex items-center space-x-2">
                <span>📊</span>
                <span>Progress <span className={colors.text}>Stats</span></span>
              </h2>
            </div>
            {achievementProgress ? (
              <AchievementProgress progress={achievementProgress} />
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                  <p className="ml-4 text-sm text-slate-600">
                    Loading stats...
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 py-2 px-4 z-20 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs">
          <div className="flex items-center space-x-4 text-slate-600">
            <span>Achievement System</span>
            <span>|</span>
            <span className="flex items-center space-x-2">
              <span>Progress:</span>
              <span className={`${colors.text} font-semibold`}>
                {achievementProgress?.unlocked || 0}/{achievementProgress?.total || 0}
              </span>
            </span>
          </div>
          <div className="text-slate-500">
            Mundus Novus v0.1.0
          </div>
        </div>
      </div>
    </div>
  );
}
